import React, { useMemo } from 'react';
import Modal from 'react-modal';
import { isNull } from 'lodash/fp';

import { ANALYSIS_MARGIN, ANALYSIS_VIEW_BUTTONS, BUTTON_WRAPPER, DATASET_INSTANCE_HEADER, NAV_BAR, PAGE_BORDER } from '../../../../hooks/useSplitView';
import { useWindowResize } from '../../../../hooks/useWindowResize';
import styles from './DocumentNettingAnalysis.module.scss';
import { NettingResponseData } from '../../../opinions/netting-engine/store';
import { PageLoadingDots } from '../../../shared/loading-dots/PageLoadingDots';
import { NettingEngineResults } from '../../../opinions/netting-engine/NettingEngineResults';
import { Button } from '../../../shared/button/Button';

interface ResultsModalProps {
    nettingResults: NettingResponseData | null;
    isCalculatingResults: boolean;
    closeModal: () => void;
    isOpen: boolean;
}

export const ResultsModal: React.FC<ResultsModalProps> = ({ closeModal, isOpen, isCalculatingResults, nettingResults }) => {
    const widthOffset = (ANALYSIS_MARGIN * 2) + ANALYSIS_VIEW_BUTTONS;
    const heightOffset = ANALYSIS_MARGIN + PAGE_BORDER + NAV_BAR;
    const [screenWidth, screenHeight] = useWindowResize();

    const width = useMemo(() => screenWidth - widthOffset, [screenWidth, widthOffset]);

    const height = useMemo(() => screenHeight - heightOffset, [screenHeight, heightOffset]);
    const leftOffset = useMemo(() => `${ANALYSIS_VIEW_BUTTONS + ANALYSIS_MARGIN}px`, []);
    const contentTop = `${DATASET_INSTANCE_HEADER + NAV_BAR + ANALYSIS_MARGIN}px`;
    const contentHeight = useMemo(() => height - BUTTON_WRAPPER, [height]);

    const resultsContent = useMemo(() => {
        if (isCalculatingResults || isNull(nettingResults)) {
            return <PageLoadingDots />;
        }
        return (
            <div className={styles.resultsWrapper}>
                <NettingEngineResults
                    results={nettingResults}
                    isInstanceView
                    height={contentHeight}
                    width={width}
                />
                <div className={styles.resultsButtonWrapper}>
                    <Button label='Close' onClick={closeModal} />
                </div>
            </div>
        );
    }, [isCalculatingResults, nettingResults, contentHeight, width, closeModal]);

    return (
        <Modal
            isOpen={isOpen}
            className={styles.resultsModal}
            ariaHideApp={false}
            style={{
                overlay: {
                    display: 'flex',
                    zIndex: 9,
                    position: 'absolute',
                    left: leftOffset,
                    top: `${NAV_BAR}px`,
                    width: `${width}px`,
                    height: `${height}px`,
                    borderRadius: '5px'
                },
                content: {
                    top: contentTop,
                    width: `${width}px`,
                    height: `${height}px`
                }
            }}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
            onRequestClose={closeModal}
        >
            {resultsContent}
        </Modal>
    );
};
